import React from 'react';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';

import Accordion from 'ui/accordion';
import Tooltip from 'ui/tooltip';
import './Stats.scss';
import { getStats } from 'logic/stats';
import { formatNumber, replaceReact, _R } from 'utils/format';
import EditingContext from 'store/editingContext';
import TooltipBuilder, { colored } from 'tooltips/builder';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import ExportLine from './export';

const _L = Locale("ui-stats.js");

function formatTip(text, stat, stats) {
  const values = stats.getValue(stat.stat);
  const getValue = index => (typeof values === "number" ? values : values[parseInt(index) > 1 ? "max" : "min"]);
  return replaceReact(text, /\$(\d)|{([^};]*)(?:;(\+)?(\d)?(%)?(x)?)?}/g, (m, index, expr, plus, precision, percent, times) => {
    if (index) {
      return colored((stat.plus ? "+" : "") + formatNumber(getValue(index), stat.decimal, 10000) + (stat.percent ? "%" : ""), "green");
    } else {
      expr = expr.replace(/\$(\d)/g, (m, index) => getValue(index));
      expr = stats.execString(expr);
      precision = parseInt(precision);
      if (typeof expr === "number") {
        expr = formatNumber(expr, precision, 10000);
      } else {
        expr = formatNumber(expr.min, precision, 10000) + "-" + formatNumber(expr.max, precision, 10000);
      }
      return colored((plus || "") + expr + (percent || "") + (times || ""), "green");
    }
  });
}

function StatTooltip({ stat, stats, follower }) {
  const [, setHighlight] = EditingContext.useState("highlightItems");
  const profile = Store.useProfile();

  React.useLayoutEffect(() => {
    const src = stat.sourcestat || stat.stat;
    const sources = {...stats.sources[src]};
    if (Data.stats[src] && Data.stats[src].resist && stats.sources.resall) {
      Object.assign(sources, stats.sources.resall);
    }
    setHighlight(sources);
    return () => setHighlight(null);
  }, [setHighlight, stat, stats]);

  const lines = stat.tooltip.split(/\n( ?\* )?/);
  const builder = new TooltipBuilder(formatTip(lines[0], stat, stats));
  for (let i = 1; lines[i + 1]; i += 2) {
    builder.line(formatTip(lines[i + 1], stat, stats), !!lines[i]);
  }
  if (stat.suffix) {
    builder.line(formatTip(stat.suffix, stat, stats));
  }

  const src = stat.sourcestat || stat.stat;
  if (Data.stats[src]) {
    const statInfo = Data.stats[src];
    const sources = {};
    const limits = {};
    const items = (follower ? profile.followerItems : profile.items);
    if (src !== "damage" && (!statInfo.classes || statInfo.classes.includes(stats.charClass))) {
      for (const slot in items) {
        const item = items[slot];
        const affixes = item && Data.getItemAffixesById(item.id, item.ancient, true);
        if (affixes && affixes[src] && affixes[src].min && affixes[src].max) {
          sources[slot] = 0;
          limits[slot] = affixes[src];
        }
      }
    }
    if (statInfo.resist && stats.sources.resall) {
      Object.assign(sources, stats.sources.resall);
      for (const slot in stats.sources.resall) {
        const item = items[slot];
        const affixes = item && Data.getItemAffixesById(item.id, item.ancient, true);
        if (affixes && affixes.resall && affixes.resall.min && affixes.resall.max) {
          limits[slot] = affixes.resall;
        }
      }
    }
    if (stats.sources[src]) {
      Object.assign(sources, stats.sources[src]);
    }
    const exlist = (statInfo.special ? stats.getSpecial(src, null, false, null) : []);

    if (Object.keys(sources).length || exlist.length) {
      builder.line((stat.sourcename || _L`Increased by`) + ":");
      const decimal = (stat.sourcedecimal == null ? stat.decimal : stat.sourcedecimal);
      const percent = ((stat.sourcepercent == null ? stat.percent : stat.sourcepercent) ? "%" : "");
      const fmtNumber = value => formatNumber(value, decimal, 10000) + percent;
      const fmtColor = value => colored(fmtNumber(value), value >= 0 ? "green" : "red");

      for (const src in sources) {
        if (Data.sourceNames[src]) {
          const limit = limits[src] && `${fmtNumber(limits[src].min)}-${fmtNumber(limits[src].max)}`;
          if (sources[src]) {
            builder.line(_R`${colored(Data.sourceNames[src], "gold")}: ${fmtColor(sources[src])}${limit ? colored(` (${limit})`, "gray") : ""}`, false);
          } else if (limit) {
            builder.line(_R`${colored(Data.sourceNames[src], "gray")}: ${colored(limit, "gray")}`, false);
          }
        }
      }
      for (const ex of exlist) {
        builder.line(_R`${colored(ex[0], "gold")}: ${fmtColor(ex[1])}`, false);
      }
    }
  }

  return builder.build();
}

function StatLine({ stat, stats, search, follower }) {
  if (stat.separator) return search ? null : <li className="d3p-stat-separator"/>;
  const name = stat.name || Data.stats[stat.stat].name;
  if (search && !name.toLowerCase().includes(search)) return null;
  const value = stats.getValue(stat.stat);
  if (stat.collapse && !value) return null;
  let zero, text;
  if (typeof value === "number") {
    zero = !value;
    text = formatNumber(value, stat.decimal, 10000);
  } else {
    zero = !value.min && !value.max;
    text = formatNumber(value.min, stat.decimal, 10000) + "-" + formatNumber(value.max, stat.decimal, 10000);
  }
  if (stat.plus) text = "+" + text;
  if (stat.percent) text += "%";
  return (
    <Tooltip tooltip={<StatTooltip stat={stat} stats={stats} follower={follower}/>} align="left" touchAction="show">
      <li className={zero ? "d3p-stat-zero" : null}>
        <span>{name}</span>
        <span>{text}</span>
      </li>
    </Tooltip>
  );
}

function StatSection({ index, section, search, follower }) {
  const [visible, setVisible] = React.useState(true);
  const onToggle = React.useCallback(() => setVisible(visible => !visible), []);

  const state = Store.useState();
  let stats = getStats(state);
  if (follower) stats = stats.follower;
  if (!stats) return null;

  const statList = section.stats.filter(stat => {
    if (stat.stat && !stat.name && !Data.stats[stat.stat]) return false;
    if (['str', 'dex', 'int', 'vit'].includes(stat.stat) && !follower) return true;
    if (stat.classes && !stat.classes.includes(stats.charClass)) return false;
    if (Data.stats[stat.stat] && Data.stats[stat.stat].classes && !Data.stats[stat.stat].classes.includes(stats.charClass)) return false;
    return true;
  }).map((stat, idx) => <StatLine key={idx} stat={stat} stats={stats} search={search} follower={follower}/>);

  if (search) {
    return (
      <div className="d3p-StatSection">
        <ul>{statList}</ul>
      </div>
    );
  }

  return (
    <Draggable draggableId={section.name} index={index}>
      {provided => (
        <div className="d3p-StatSection" {...provided.draggableProps} ref={provided.innerRef}>
          <h3 onClick={onToggle} {...provided.dragHandleProps}>{section.name}</h3>
          <Accordion visible={visible}>
            <ul>
              {statList}
              {section.export && <ExportLine/>}
            </ul>
          </Accordion>
        </div>
      )}
    </Draggable>
  );
}

function SearchBox({ value, onChange }) {
  const [focused, setFocus] = React.useState("");
  const onSetValue = React.useCallback(e => onChange(e.target.value), [onChange]);
  const onClear = React.useCallback(() => onChange(""), [onChange]);
  const onFocus = React.useCallback(() => setFocus(true), []);
  const onBlur = React.useCallback(() => setFocus(false), []);
  return (
    <div className="d3p-StatsSearch">
      <input type="text" value={value} onChange={onSetValue} onFocus={onFocus} onBlur={onBlur} placeholder={focused ? null : _L`Search`}/>
      {value.length > 0 && <span className="d3p-search-clear" onClick={onClear}>
        <FontAwesomeIcon icon={faTimesCircle}/>
      </span>}
    </div>
  );
}

const StatsColumn = ({ name, sections, search, follower }) => (
  <Droppable droppableId={name}>
    {provided => (
      <div className="d3p-StatsColumn" {...provided.droppableProps} ref={provided.innerRef}>
        {sections.map((section, idx) => <StatSection key={section.name} index={idx} section={section} search={search} follower={follower}/>)}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
);

function getStatList() {
  const sections = Data.localeTable.uiStats.slice();
  const pos = sections[1].stats.findIndex(line => line.separator);
  sections[1] = {...sections[1]};
  sections[1].stats = [
    ...sections[1].stats.slice(0, pos),
    ...Object.entries(Data.stats).filter(([, info]) => info.skill && !info.sanctified).map(([stat, info]) => ({
      name: _L`${info.skill} Damage Increase`,
      stat,
      decimal: 2,
      percent: true,
      classes: info.classes,
      collapse: true,
      tooltip: _L`${info.skill} Damage Increase: $1\n* Increases Damage dealt by this skill.`,
    })),
    ...sections[1].stats.slice(pos)
  ];
  sections.push({
    name: _L`Export`,
    stats: [],
    export: true,
  });

  return {
    column1: sections,
    column2: [],
    column1f: Data.localeTable.uiStatsFollower?.slice() || [],
    column2f: [],
  };
}

export default React.memo(function Stats() {
  const [search, setSearch] = React.useState("");
  const [dragging, setDragging] = React.useState(false);
  const [columns, setColumns] = React.useState(getStatList);
  const follower = (EditingContext.useStateView("paperdollMode") === "follower") && Data.localeTable.uiStatsFollower != null;

  const onBeforeCapture = React.useCallback(() => {
    setDragging(true);
  }, []);
  const onDragEnd = React.useCallback(({ destination, source }) => {
    setDragging(false);
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    setColumns(columns => {
      columns = {...columns};
      columns[source.droppableId] = columns[source.droppableId].slice();
      if (destination.droppableId !== source.droppableId) {
        columns[destination.droppableId] = columns[destination.droppableId].slice();
      }
      const items = columns[source.droppableId].splice(source.index, 1);
      columns[destination.droppableId].splice(destination.index, 0, ...items);
      return columns;
    });
  }, []);

  return (
    <React.Fragment>
      <SearchBox value={search} onChange={setSearch}/>
      <DragDropContext onBeforeCapture={onBeforeCapture} onDragEnd={onDragEnd}>
        <div className={classNames("d3p-StatsList", {"d3p-has-search": search, "d3p-two-column": columns.column2.length > 0 || dragging, "d3p-dragging": dragging})}>
          <div className="d3p-StatsContainer">
            {!follower && <StatsColumn name="column1" sections={columns.column1} search={search.toLowerCase()}/>}
            {!follower && (columns.column2.length > 0 || dragging) && <StatsColumn name="column2" sections={columns.column2} search={search.toLowerCase()}/>}
            {!!follower && <StatsColumn name="column1f" sections={columns.column1f} search={search.toLowerCase()} follower={true}/>}
            {!!follower && (columns.column2f.length > 0 || dragging) && <StatsColumn name="column2f" sections={columns.column2f} search={search.toLowerCase()} follower={true}/>}
          </div>
        </div>
      </DragDropContext>
    </React.Fragment>
  );
});
